import { lockPageScroll } from '../../../../assets/js/utils/scroll-lock';
import { initializeReviewUpload } from '../../../../assets/js/utils/review-upload';

const WRAPPER_SELECTOR = '[data-component="product-reviews"]';
const LIST_SELECTOR = '[data-product-reviews-list]';
const PAGINATION_SELECTOR = '[data-product-reviews-pagination]';
const PAGE_BUTTON_SELECTOR = '[data-product-reviews-page]';
const OPEN_MODAL_SELECTOR = '[data-product-reviews-open-modal]';
const CLOSE_MODAL_SELECTOR = '[data-product-reviews-close-modal]';
const MODAL_SELECTOR = '[data-product-reviews-modal]';
const STAR_LINK_SELECTOR = '#respond p.stars a';
const STARS_SELECTOR = '#respond p.stars';
const RATING_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', ' ', 'Enter']);

const parsePositiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
};

const normalizeRatingValue = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1 || parsed > 5) {
        return 0;
    }

    return parsed;
};

const parseStarRatingFromClass = (starElement) => {
    if (!starElement) {
        return 0;
    }

    const starClass = Array.from(starElement.classList).find((className) => /^star-[1-5]$/.test(className));

    if (!starClass) {
        return 0;
    }

    return normalizeRatingValue(starClass.replace('star-', ''));
};

const applyStarsRatingState = (starsWrapper, ratingValue) => {
    if (!starsWrapper) {
        return;
    }

    const normalizedRating = normalizeRatingValue(ratingValue);
    const starLinks = starsWrapper.querySelectorAll('a');

    if (normalizedRating > 0) {
        starsWrapper.dataset.rating = String(normalizedRating);
    } else {
        delete starsWrapper.dataset.rating;
    }

    starsWrapper.classList.toggle('selected', normalizedRating > 0);

    starLinks.forEach((starLink) => {
        const starValue = parseStarRatingFromClass(starLink);
        const isActive = normalizedRating > 0 && starValue === normalizedRating;

        starLink.classList.toggle('active', isActive);
        starLink.setAttribute('aria-checked', isActive ? 'true' : 'false');
        starLink.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    if (normalizedRating === 0 && starLinks.length) {
        starLinks[0].setAttribute('tabindex', '0');
    }

    if (starsWrapper.closest('.ProductReviewDialog')) {
        const field = starsWrapper.closest('.comment-form-rating');
        const select = field?.querySelector('select');
        if (!select) return;
        let value = field.querySelector('.ProductReviewDialog__rating-value');
        if (!value) {
            value = document.createElement('span');
            value.className = 'ProductReviewDialog__rating-value';
            value.setAttribute('aria-live', 'polite');
            field.append(value);
        }
        const label = select.selectedOptions[0]?.textContent || '';
        const text = normalizedRating ? `${label} · ${normalizedRating}/5` : label;
        if (value.textContent !== text) value.textContent = text;
    }
};

const synchronizeComponentStars = (component) => {
    const starsWrappers = component.querySelectorAll(STARS_SELECTOR);

    starsWrappers.forEach((starsWrapper) => {
        const respond = starsWrapper.closest('#respond');
        const ratingSelect = respond ? respond.querySelector('#rating') : null;
        const ratingValue = ratingSelect ? ratingSelect.value : '';

        applyStarsRatingState(starsWrapper, ratingValue);
    });
};

const setLoadingState = (component, isLoading) => {
    component.classList.toggle('is-loading', isLoading);
    component.setAttribute('aria-busy', String(isLoading));
};

const fetchReviewsPage = async (component, pageNumber) => {
    if (!window.jsData || !window.jsData.ajaxUrl) {
        throw new Error('Missing jsData.ajaxUrl');
    }

    const productId = parsePositiveInteger(component.dataset.productId, 0);
    const action = component.dataset.action;
    const nonce = component.dataset.nonce || '';

    if (!productId || !action || !nonce) {
        throw new Error('Missing required payload values');
    }

    const payload = new URLSearchParams();
    payload.append('action', action);
    payload.append('product_id', String(productId));
    payload.append('page', String(pageNumber));
    payload.append('nonce', nonce);

    const response = await fetch(window.jsData.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body: payload.toString(),
    });

    if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
    }

    return response.json();
};

const replaceReviewsMarkup = (component, responseData) => {
    const list = component.querySelector(LIST_SELECTOR);
    const pagination = component.querySelector(PAGINATION_SELECTOR);

    if (list && typeof responseData.list_html === 'string') {
        list.innerHTML = responseData.list_html;
    }

    if (pagination && typeof responseData.pagination_html === 'string') {
        pagination.innerHTML = responseData.pagination_html;
    }

    const currentPage = parsePositiveInteger(responseData.current_page, 1);
    component.dataset.currentPage = String(currentPage);
};

const bindReviewsPagination = (component) => {
    let isRequestPending = false;

    component.addEventListener('click', async (event) => {
        const target = event.target.closest(PAGE_BUTTON_SELECTOR);

        if (!target) {
            return;
        }

        event.preventDefault();

        if (target.hasAttribute('disabled') || isRequestPending) {
            return;
        }

        const nextPage = parsePositiveInteger(target.dataset.productReviewsPage, 0);
        const currentPage = parsePositiveInteger(component.dataset.currentPage, 1);

        if (!nextPage || nextPage === currentPage) {
            return;
        }

        isRequestPending = true;
        setLoadingState(component, true);

        try {
            const response = await fetchReviewsPage(component, nextPage);

            if (!response || response.success !== true || !response.data) {
                return;
            }

            replaceReviewsMarkup(component, response.data);
        } catch (error) {
            // Keep graceful degradation: on request failure the currently rendered
            // opinions remain visible and interactive.
            console.error(error);
        } finally {
            isRequestPending = false;
            setLoadingState(component, false);
        }
    });
};

const bindReviewsModal = (component) => {
    const modal = component.querySelector(MODAL_SELECTOR);
    if (!modal) {
        return;
    }

    if (typeof HTMLDialogElement !== 'undefined' && modal instanceof HTMLDialogElement) {
        let unlock;
        let trigger;
        component.querySelectorAll(OPEN_MODAL_SELECTOR).forEach((button) => {
            button.addEventListener('click', () => {
                if (modal.open) return;
                trigger = button;
                unlock = lockPageScroll();
                modal.showModal();
                modal.querySelector('[data-review-dialog-title]')?.focus({ preventScroll: true });
                button.setAttribute('aria-expanded', 'true');
            });
        });
        component.querySelectorAll(CLOSE_MODAL_SELECTOR).forEach((button) => button.addEventListener('click', () => modal.close()));
        modal.addEventListener('close', () => {
            unlock?.(); unlock = null;
            trigger?.setAttribute('aria-expanded', 'false');
            trigger?.focus({ preventScroll: true });
        });
        let outside = false;
        const backdrop = (event) => {
            const rect = modal.getBoundingClientRect();
            return event.target === modal && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
        };
        modal.addEventListener('pointerdown', (event) => { outside = backdrop(event); });
        modal.addEventListener('click', (event) => { if (outside && backdrop(event)) modal.close(); outside = false; });
        return;
    }

    const closeModal = (event) => {
        if (event) {
            event.preventDefault();
        }

        modal.classList.remove('open');
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.style.overflowY = '';
    };

    const openModal = (event) => {
        if (event) {
            event.preventDefault();
        }

        modal.classList.add('open');
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.style.overflowY = 'hidden';
    };

    const openButtons = component.querySelectorAll(OPEN_MODAL_SELECTOR);
    const closeButtons = component.querySelectorAll(CLOSE_MODAL_SELECTOR);
    const modalInner = modal.querySelector('.ProductReviewsModal__inner');

    openButtons.forEach((button) => {
        button.addEventListener('click', openModal);
    });

    closeButtons.forEach((button) => {
        button.addEventListener('click', closeModal);
    });

    modal.addEventListener('click', (event) => {
        const clickedInsideInner = modalInner && modalInner.contains(event.target);
        if (!clickedInsideInner) {
            closeModal(event);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.classList.contains('open')) {
            closeModal();
        }
    });
};

const selectRating = (starLink) => {
    const starsWrapper = starLink.closest('p.stars');
    const ratingValue = parseStarRatingFromClass(starLink);
    const ratingSelect = starLink.closest('#respond')?.querySelector('#rating');
    if (!starsWrapper || !ratingValue || !ratingSelect) return;
    ratingSelect.value = String(ratingValue);
    ratingSelect.dispatchEvent(new Event('change', { bubbles: true }));
    applyStarsRatingState(starsWrapper, ratingValue);
};

const bindReviewsRating = (component) => {
    component.addEventListener(
        'click',
        (event) => {
            const starLink = event.target.closest(STAR_LINK_SELECTOR);

            if (!starLink || !component.contains(starLink)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            selectRating(starLink);
        },
        true,
    );

    component.addEventListener('keydown', (event) => {
        const star = event.target.closest(STAR_LINK_SELECTOR);
        if (!star || !RATING_KEYS.has(event.key)) return;
        event.preventDefault(); event.stopPropagation();
        const stars = [...star.closest('p.stars').querySelectorAll('a')];
        let index = stars.indexOf(star);
        if (['ArrowRight', 'ArrowDown'].includes(event.key)) index = (index + 1) % stars.length;
        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) index = (index + stars.length - 1) % stars.length;
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = stars.length - 1;
        selectRating(stars[index]);
        stars[index].focus({ preventScroll: true });
    }, true);
    // WooCommerce also delegates arrow keys on keyup; do not select a second star.
    component.addEventListener('keyup', (event) => {
        if (event.target.closest(STAR_LINK_SELECTOR) && RATING_KEYS.has(event.key)) {
            event.preventDefault(); event.stopPropagation();
        }
    }, true);

    synchronizeComponentStars(component);

    if (typeof MutationObserver === 'undefined') {
        return;
    }

    const observer = new MutationObserver((mutations) => {
        const shouldSync = mutations.some((mutation) => mutation.type === 'childList' && mutation.addedNodes.length > 0);

        if (shouldSync) {
            synchronizeComponentStars(component);
        }
    });

    observer.observe(component, {
        childList: true,
        subtree: true,
    });
};

document.addEventListener('DOMContentLoaded', () => {
    const reviewComponents = document.querySelectorAll(WRAPPER_SELECTOR);

    reviewComponents.forEach((component) => {
        bindReviewsRating(component);
        bindReviewsPagination(component);
        bindReviewsModal(component);
        component.querySelectorAll('.ProductReviewDialog [data-review-upload]').forEach(initializeReviewUpload);
    });
});
